// Helper for the "Zásilky" (shipments) module: fetch wrapper for /api/shipments and its error codes.
// Every reply is { success, data?, error?, code? }; api() resolves with `data` and throws an Error
// carrying .status, .code and .data (e.g. data.retryAfterSeconds, data.attemptsLeft).

export const API = '/api/shipments';

export const CODES = {
  VERIFICATION_REQUIRED: 'VERIFICATION_REQUIRED',
  NO_ACCESS: 'NO_ACCESS',
  RATE_LIMITED: 'RATE_LIMITED',
  LOCKED: 'LOCKED',
  INVALID_CODE: 'INVALID_CODE',
  CODE_EXPIRED: 'CODE_EXPIRED',
  MAIL_NOT_CONFIGURED: 'MAIL_NOT_CONFIGURED',
  MAIL_FAILED: 'MAIL_FAILED',
  NETWORK: 'NETWORK',
  NOT_FOUND: 'NOT_FOUND',
  CONFLICT: 'CONFLICT',
  VALIDATION: 'VALIDATION',
  MERGED: 'MERGED',
  MAILBOX_NOT_ALLOWED: 'MAILBOX_NOT_ALLOWED', // 403 from POST /mailboxes: not in env.allowedMailboxes
  SHIPMENT_DELETED: 'SHIPMENT_DELETED', // 409 from POST /review/:emailId/approve: the shipment is soft-deleted, data.shipmentId
};

// Czech fallbacks, used only when the server did not send an `error` text.
const FALLBACK_BY_CODE = {
  [CODES.VERIFICATION_REQUIRED]: 'Ověření vypršelo, zadejte nový kód.',
  [CODES.NO_ACCESS]: 'Nemáte oprávnění k modulu Zásilky.',
  [CODES.RATE_LIMITED]: 'Příliš mnoho požadavků, zkuste to později.',
  [CODES.LOCKED]: 'Příliš mnoho pokusů, zkuste to později.',
  [CODES.INVALID_CODE]: 'Nesprávný kód.',
  [CODES.CODE_EXPIRED]: 'Platnost kódu vypršela. Pošlete si nový kód.',
  [CODES.MAIL_NOT_CONFIGURED]: 'Odesílání e-mailů není nastavené. Kontaktujte správce.',
  [CODES.MAIL_FAILED]: 'E-mail s kódem se nepodařilo odeslat. Zkuste to za chvíli znovu.',
  [CODES.NETWORK]: 'Server není dostupný. Zkontrolujte připojení a zkuste to znovu.',
};

const FALLBACK_BY_STATUS = {
  400: 'Neplatný požadavek.',
  401: 'Přihlášení vypršelo, přihlaste se znovu.',
  403: 'Nemáte oprávnění k této akci.',
  404: 'Požadovaná položka nebyla nalezena.',
  429: 'Příliš mnoho požadavků, zkuste to později.',
  502: 'Služba je dočasně nedostupná. Zkuste to za chvíli znovu.',
  503: 'Služba je dočasně nedostupná. Zkuste to za chvíli znovu.',
};

// Listeners notified whenever the server says the verification is gone (idle/absolute expiry, manual lock
// from another tab). The Zásilky page uses it to flip back to the gate no matter which call noticed first.
const verificationListeners = new Set();

export function onVerificationRequired(listener) {
  verificationListeners.add(listener);
  return () => verificationListeners.delete(listener);
}

function buildError(res, json) {
  const code = json.code;
  // validate middleware: { error: "Validation failed", details: [{ msg }] } -> the specific message is more useful
  const detail = Array.isArray(json.details) && typeof json.details[0]?.msg === 'string' ? json.details[0].msg : '';
  const message = detail
    || json.error
    || FALLBACK_BY_CODE[code]
    || FALLBACK_BY_STATUS[res.status]
    || `Chyba ${res.status}`;
  const err = new Error(message);
  err.status = res.status;
  err.code = code;
  err.data = json.data; // MERGED -> { mergedIntoId }, CONFLICT -> { id } of the existing shipment
  err.details = json.details;
  return err;
}

export async function api(path, { method = 'GET', body } = {}) {
  let res;
  try {
    res = await fetch(`${API}${path}`, {
      method,
      credentials: 'include',
      headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    const err = new Error(FALLBACK_BY_CODE[CODES.NETWORK]);
    err.status = 0;
    err.code = CODES.NETWORK;
    throw err;
  }
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.success === false) {
    const err = buildError(res, json);
    if (err.code === CODES.VERIFICATION_REQUIRED) verificationListeners.forEach((fn) => fn(err));
    throw err;
  }
  return json.data;
}

export const isCode = (err, code) => err?.code === code;

// ---- typed helpers for the REST contract (docs/SHIPMENTS-API.md) ----

// "?a=1&b=2" from an object; empty values and empty arrays are skipped, arrays become comma lists
export function buildQuery(params = {}) {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    const v = Array.isArray(value) ? value.join(',') : value;
    if (v === undefined || v === null || v === '') return;
    sp.set(key, String(v));
  });
  const text = sp.toString();
  return text ? `?${text}` : '';
}

const post = (path, body) => api(path, { method: 'POST', body: body ?? {} });
const enc = encodeURIComponent;

export const shipmentsApi = {
  summary: () => api('/summary'),
  list: (params) => api(`/${buildQuery(params)}`), // -> { items, total }
  detail: (id) => api(`/${enc(id)}`),
  create: (body) => post('/', body),
  update: (id, body) => api(`/${enc(id)}`, { method: 'PATCH', body }),
  merge: (id, targetId) => post(`/${enc(id)}/merge`, { targetId }),
  remove: (id) => api(`/${enc(id)}`, { method: 'DELETE' }),
  restore: (id) => post(`/${enc(id)}/restore`),
  reprocess: (id) => post(`/${enc(id)}/reprocess`),

  reviewList: (params) => api(`/review${buildQuery(params)}`),
  reviewApprove: (emailId, corrections) => post(`/review/${enc(emailId)}/approve`, corrections ? { corrections } : {}),
  reviewReject: (emailId) => post(`/review/${enc(emailId)}/reject`),
  reviewLink: (emailId, body) => post(`/review/${enc(emailId)}/link`, body), // { shipmentId } | { create }

  notifications: (params) => api(`/notifications${buildQuery(params)}`),
  notificationRead: (id) => post(`/notifications/${enc(id)}/read`),
  notificationsReadAll: () => post('/notifications/read-all'),

  settings: () => api('/settings'),
  saveSettings: (body) => api('/settings', { method: 'PUT', body }),
  addMailbox: (mailbox) => post('/mailboxes', { mailbox }),
  setMailboxEnabled: (mailbox, enabled) => api(`/mailboxes/${enc(mailbox)}`, { method: 'PATCH', body: { enabled } }),
  syncMailbox: (mailbox) => post(`/mailboxes/${enc(mailbox)}/sync`),
  errors: (params) => api(`/errors${buildQuery(params)}`),
  retryError: (mailbox, messageId) => post(`/errors/${enc(mailbox)}/${enc(messageId)}/retry`),

  carriers: () => api('/carriers'),
  setCarrierActive: (code, isActive) => api(`/carriers/${enc(code)}`, { method: 'PATCH', body: { isActive } }),
};
